import { CachedImageData, AtlasMapValue, AtlasTextureData } from 'game-ui-types'

export interface AtlasSlice {
    // atlasMap: { [atlasName]: { [imageName]: AtlasMapValue } }
    atlasMap: Record<string, Record<string, AtlasMapValue>>
    cachedImages: Record<string, CachedImageData>

    preloadImages: (atlasUrls: string[]) => void
    handleAtlasTexturesData: (buffersData: Record<string, AtlasTextureData>) => void
    addAtlasMap: (newAtlasMap: Record<string, AtlasMapValue[]>) => void

    findElementByImageName: (name: string) => { atlasName: string; value: AtlasMapValue } | undefined
}

export const createAtlasSlice = (
    set: (fn: (state: AtlasSlice) => Partial<AtlasSlice>) => void,
    get: () => AtlasSlice
): AtlasSlice => ({
    atlasMap: {},
    cachedImages: {},
    addAtlasMap: (newAtlasMap) =>
        set((state) => {
            // Change from { [atlasName]: AtlasMapValue[] } to { [atlasName]: { [imageName]: AtlasMapValue } }
            const formattedAtlas = Object.entries(newAtlasMap).reduce(
                (newObj, [atlasName, textureArray]) => {
                    newObj[atlasName] = textureArray.reduce(
                        (newTextureObj, entry) => {
                            newTextureObj[entry.name] = entry

                            return newTextureObj
                        },
                        {} as Record<string, AtlasMapValue>
                    )

                    return newObj
                },
                {} as Record<string, Record<string, AtlasMapValue>>
            )

            return { atlasMap: { ...state.atlasMap, ...formattedAtlas } }
        }),

    preloadImages: async (atlasUrls: string[]) => {
        const { cachedImages } = get()
        const imagesToLoad = atlasUrls.filter((url) => !cachedImages[url])

        if (imagesToLoad.length === 0) return

        const imageCache: Record<string, CachedImageData> = {}

        await Promise.all(
            imagesToLoad.map(
                (url) =>
                    new Promise<void>((resolve) => {
                        const img = new Image()

                        img.crossOrigin = 'Anonymous'
                        img.src = url
                        img.onload = () => {
                            const canvas = document.createElement('canvas')

                            canvas.width = img.width
                            canvas.height = img.height

                            const ctx = canvas.getContext('2d')

                            ctx?.drawImage(img, 0, 0)

                            imageCache[url] = {
                                url: canvas.toDataURL('image/png'),
                                width: img.width,
                                height: img.height,
                            }

                            resolve()
                        }
                    })
            )
        )

        set((state: AtlasSlice) => ({
            cachedImages: { ...state.cachedImages, ...imageCache },
        }))
    },

    handleAtlasTexturesData: async (atlasTexturesData: Record<string, AtlasTextureData>) => {
        await Promise.all(
            Object.entries(atlasTexturesData).map(async ([key, atlasTextureData]) => {
                const blob = new Blob([atlasTextureData.buffer], { type: atlasTextureData.type })
                const dataUrl = await new Promise<string>((resolve) => {
                    const r = new FileReader()

                    r.onload = () => resolve(String(r.result))
                    r.readAsDataURL(blob)
                })

                set((state: AtlasSlice) => ({
                    cachedImages: {
                        ...state.cachedImages,
                        [key]: { url: dataUrl, width: atlasTextureData.width, height: atlasTextureData.height },
                    },
                }))
            })
        )
    },

    findElementByImageName: (name) => {
        let found: { atlasName: string; value: AtlasMapValue } | undefined

        for (const [atlasName, atlasTextures] of Object.entries(get().atlasMap)) {
            if (atlasTextures[name]) found = { atlasName, value: atlasTextures[name] }

            if (found) break
        }

        return found
    },
})
